import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button, Container, Field, Panel, Textarea } from '../components/site/ui';
import { useToast } from '../components/admin/overlays';
import { IconBell, IconCheckCircle, IconClock, IconMail, IconStar } from '../components/icons';

const NEXT_STEPS = [
  { icon: IconMail, label: 'Sent to the retailer within minutes' },
  { icon: IconClock, label: 'They review your evidence and respond' },
  { icon: IconBell, label: "You'll get updates by email as it progresses" }
];

const StarRow = ({ label, value, hoveredValue, onRate, onHover, onLeave }) => (
  <div className="flex items-center justify-between gap-4 border-b border-ink-100 py-2.5 last:border-b-0">
    <span className="text-sm font-600 text-ink-800 sm:text-[15px]">{label}</span>
    <div className="flex gap-0.5 sm:gap-1" onMouseLeave={onLeave}>
      {[1, 2, 3, 4, 5].map((star) => {
        const active = star <= (hoveredValue || value);
        return (
          <button
            key={star}
            type="button"
            onClick={() => onRate(star)}
            onMouseEnter={() => onHover(star)}
            aria-label={`${star} out of 5`}
            className="rounded-md p-1 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
          >
            <IconStar
              size={25}
              filled={active}
              strokeWidth={1.5}
              className={`transition-colors ${active ? 'text-gold-500' : 'text-ink-300'}`}
            />
          </button>
        );
      })}
    </div>
  </div>
);

const FeedbackPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const caseId = location.state?.caseId;
  const fromMerchant = location.state?.fromMerchant || false;

  const [ratings, setRatings] = useState({ easeOfUse: 0, speedOfUse: 0, overall: 0 });
  const [hovered, setHovered] = useState({ easeOfUse: 0, speedOfUse: 0, overall: 0 });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!caseId) {
      navigate('/', { replace: true });
    }
  }, [caseId, navigate]);

  const handleRate = (category, value) => {
    setRatings(prev => ({ ...prev, [category]: value }));
  };

  const handleHover = (category, value) => {
    setHovered(prev => ({ ...prev, [category]: value }));
  };

  const handleLeave = (category) => {
    setHovered(prev => ({ ...prev, [category]: 0 }));
  };

  const allRated = ratings.easeOfUse > 0 && ratings.speedOfUse > 0 && ratings.overall > 0;

  // The case is already confirmed in this page's own sidebar, so the only
  // thing worth announcing after this is the feedback itself — fired as a
  // toast (which, since AdminOverlaysProvider now wraps the whole app in
  // App.js, survives the navigate below) rather than a second banner on
  // whatever page comes next.
  const handleSubmitFeedback = async () => {
    if (!allRated) return;
    setSubmitting(true);
    try {
      await api.post('/api/feedback', {
        caseId,
        easeOfUse: ratings.easeOfUse,
        speedOfUse: ratings.speedOfUse,
        overall: ratings.overall,
        comment: comment.trim() || undefined,
        source: fromMerchant ? 'merchant' : 'customer'
      });
      toast('Thanks for your feedback!', { tone: 'success' });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast('Could not submit your feedback, but your report is still in.', { tone: 'error' });
    } finally {
      setSubmitting(false);
    }
    navigate('/');
  };

  const handleSkip = () => {
    navigate('/');
  };

  if (!caseId) return null;

  return (
    <Container width="full" className="py-7 sm:py-9">
      {/* Same rail + panel proportions as ReportPage's own steps, rather than
          a narrow centered card — there's a feedback form to fill this width
          with, not just a one-line confirmation. */}
      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,19rem)_minmax(0,1fr)] lg:gap-12">
        <aside className="lg:sticky lg:top-28 lg:pt-1">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-success-50 text-success-600 ring-1 ring-inset ring-success-100">
            <IconCheckCircle size={28} strokeWidth={1.5} />
          </span>
          <p className="mt-5 text-[11px] font-700 uppercase tracking-[0.14em] text-brand-600">
            {fromMerchant ? 'Action complete' : 'Report submitted'}
          </p>
          <h1 className="mt-2 font-display text-[26px] font-800 leading-[1.12] tracking-tight text-ink-900 sm:text-[30px]">
            {fromMerchant ? 'Action taken successfully.' : "You're all set."}
          </h1>
          <p className="mt-2.5 text-[15px] leading-relaxed text-ink-600">
            {fromMerchant ? (
              'The customer will be notified.'
            ) : (
              <>
                Case <span className="font-700 text-ink-800">#{caseId}</span> is on its way to the
                retailer.
              </>
            )}
          </p>

          {!fromMerchant && (
            <ul className="mt-7 space-y-3.5 border-t border-ink-200 pt-5">
              {NEXT_STEPS.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-2.5 text-sm text-ink-600">
                  <Icon size={15} className="shrink-0 text-brand-600" />
                  {label}
                </li>
              ))}
            </ul>
          )}
        </aside>

        <Panel className="p-5 sm:p-6">
          <h2 className="font-display text-xl font-700 tracking-tight text-ink-900 sm:text-2xl">
            How was your experience with ParcelBuddy?
          </h2>
          <p className="mt-1.5 text-sm text-ink-600">
            You used our service today — rating it helps us improve the platform.
            <span className="mt-0.5 block text-xs text-ink-500">
              Where 1 is poor and 5 is excellent.
            </span>
          </p>

          <div className="mt-4 max-w-lg">
            <StarRow
              label="Ease of use"
              value={ratings.easeOfUse}
              hoveredValue={hovered.easeOfUse}
              onRate={(v) => handleRate('easeOfUse', v)}
              onHover={(v) => handleHover('easeOfUse', v)}
              onLeave={() => handleLeave('easeOfUse')}
            />
            <StarRow
              label="Speed of use"
              value={ratings.speedOfUse}
              hoveredValue={hovered.speedOfUse}
              onRate={(v) => handleRate('speedOfUse', v)}
              onHover={(v) => handleHover('speedOfUse', v)}
              onLeave={() => handleLeave('speedOfUse')}
            />
            <StarRow
              label="Overall"
              value={ratings.overall}
              hoveredValue={hovered.overall}
              onRate={(v) => handleRate('overall', v)}
              onHover={(v) => handleHover('overall', v)}
              onLeave={() => handleLeave('overall')}
            />
          </div>

          <Field label="Comment" htmlFor="feedback-comment" hint="Optional" className="mt-4 max-w-lg">
            <Textarea
              id="feedback-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={1000}
              className="resize-none"
              placeholder="Tell us about your experience…"
            />
          </Field>

          {/* Submit and Skip share a row instead of stacking, so both are
              always visible without scrolling the panel. */}
          <div className="mt-5 flex items-center gap-5">
            <Button
              onClick={handleSubmitFeedback}
              disabled={!allRated || submitting}
              loading={submitting}
              size="lg"
              className="sm:px-10"
            >
              {submitting ? 'Submitting…' : 'Submit feedback'}
            </Button>
            <button
              type="button"
              onClick={handleSkip}
              className="rounded-lg px-2 py-2 text-sm font-medium text-ink-500 transition-colors hover:text-ink-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
            >
              Skip
            </button>
          </div>
        </Panel>
      </div>
    </Container>
  );
};

export default FeedbackPage;
